import { MAGZTER_GOLD_SLUG, PRODUCT_TYPE_MAGAZINE, SaleProduct } from './types';

/**
 * Who may not see what.
 *
 * This decision cannot move to the server, and that is the whole reason it looks like
 * this. The sale JSON is cached by CloudFront and served to everybody, so it has to
 * contain the full catalogue — `section.php` explicitly resets the restriction session
 * flags when CloudFront is the one asking. The visitor's restrictions therefore travel
 * separately, on the page, and are reconciled against the shared payload here.
 *
 * Restrictions arrive as *product ids*, and every one of them must win. Getting this
 * wrong shows a customer a title we are contractually not allowed to sell them.
 */
export type Restrictions = {
  /**
   * Products this visitor must not be shown at all: search-alert restrictions plus the
   * sale's own exclusions (titles they have already bought on this offer).
   */
  restrictedProductIds: number[];
  /**
   * Hot-zone products. Empty when the visitor is not in a hot zone. `['*']` — the
   * server's sentinel — means "in a hot zone, with no explicit list", and then every
   * product *flagged* `hide_from_hotzones` goes.
   */
  hotzoneProductIds: (number | '*')[];
};

/**
 * Magzter Gold rides in the payload as an ordinary product but is never a tile — the
 * grid hoists it into its own card above the products, which is what `MagzterGoldPromo`
 * draws and what the legacy `categories.js` did with the same `slug` test.
 */
export function isMagzterGold(product: SaleProduct): boolean {
  return product.slug === MAGZTER_GOLD_SLUG;
}

/**
 * Whether this visitor is barred from being shown this title at all.
 *
 * Split out because the hoisted Magzter Gold card has to ask the same question the grid
 * does, and it cannot ask `shouldHideProduct` — that answers yes for Magzter Gold by
 * definition. One implementation, two callers.
 */
export function isRestricted(product: SaleProduct, restrictions: Restrictions): boolean {
  if (restrictions.restrictedProductIds.indexOf(product.id) >= 0) {
    return true;
  }

  const { hotzoneProductIds } = restrictions;

  return (
    product.attributes.hz &&
    hotzoneProductIds.length > 0 &&
    (hotzoneProductIds[0] === '*' || hotzoneProductIds.indexOf(product.id) >= 0)
  );
}

export function shouldHideProduct(product: SaleProduct, restrictions: Restrictions): boolean {
  if (isMagzterGold(product)) {
    return true;
  }

  if (isRestricted(product, restrictions)) {
    return true;
  }

  // A magazine with no purchasable term has nothing to sell. Its form would render a
  // Term dropdown with no options and an Add to Cart that 400s.
  if (product.product_type === PRODUCT_TYPE_MAGAZINE && (product.restricted_terms || []).length === 0) {
    return true;
  }

  return false;
}

/**
 * Normalises what the page hands us.
 *
 * The props come straight off PHP arrays, so an id can arrive as `"1234"` and the
 * hot-zone list can arrive as `['*']`. Comparing those against the JSON's numeric ids
 * with `indexOf` silently matches nothing, which fails *open* — the restricted product
 * is shown. Coercing once, here, is what stops that.
 */
export function normalizeRestrictions(
  restrictedProductIds: unknown,
  hotzoneProductIds: unknown
): Restrictions {
  return {
    restrictedProductIds: toIdList(restrictedProductIds).filter(
      (id): id is number => typeof id === 'number'
    ),
    hotzoneProductIds: toIdList(hotzoneProductIds),
  };
}

function toIdList(value: unknown): (number | '*')[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (entry === '*') {
        return '*' as const;
      }

      const id = typeof entry === 'number' ? entry : parseInt(String(entry), 10);

      return Number.isFinite(id) ? id : null;
    })
    .filter((entry): entry is number | '*' => entry !== null);
}
